import { Action, createHook, createStore } from "react-sweet-state";

import { captureSentryException } from "utility/capture_sentry_exception";

import { unauthenticatedPostFetch } from "../utility/fetch";

// -----------------------------------------------------------------------------

const initialState: LoginStoreState = {
  checkEmail: false,
  hasSSORedirect: false,
  submitting: false,
  registering: false,
  loginError: "",
};

// -----------------------------------------------------------------------------

export const useLoginStore = createHook(
  createStore({
    initialState,
    actions: {
      login,
      register,
      resetErrors,
    },
  })
);

// -----------------------------------------------------------------------------

function login(email: string): Action<LoginStoreState> {
  return async ({ setState, getState }) => {
    if (getState().submitting) {
      return;
    }

    //
    try {
      setState({ submitting: true, loginError: "", registerError: "" });
      const responsePayload = await unauthenticatedPostFetch(
        "api/auth/loginUser",
        {
          email,
        },
        [200, 403]
      );

      //
      switch (true) {
        case responsePayload.error:
          setState({
            submitting: false,
            checkEmail: false,
            hasSSORedirect: false,
            loginError: responsePayload.message,
          });
          break;

        case responsePayload.message === "Email sent":
          setState({
            submitting: false,
            checkEmail: true,
            hasSSORedirect: false,
            loginError: "",
          });
          break;

        case responsePayload.redirectURL !== undefined:
          setState({
            submitting: false,
            checkEmail: false,
            hasSSORedirect: true,
            loginError: "",
          });
          break;

        default:
          setState({
            submitting: false,
            checkEmail: false,
            loginError: "An unknown error occurred",
          });
      }

      //
      return responsePayload;
    } catch (error) {
      captureSentryException(error, "Failed to login");
    }
  };
}

function register(
  email: string,
  fullName: string,
  orgName: string
): Action<LoginStoreState> {
  return async ({ setState, getState }) => {
    if (getState().registering) {
      return;
    }
    setState({ registering: true, loginError: "", registerError: "" });
    try {
      const responsePayload = await unauthenticatedPostFetch<{
        error: boolean;
        message?: string;
      }>(
        "api/auth/registerUser",
        {
          email,
          full_name: fullName,
          org_name: orgName,
        },
        [201, 400]
      );

      switch (true) {
        case responsePayload.error:
          setState({
            registeredEmail: undefined,
            registerError: responsePayload.message,
            registering: false,
          });
          break;

        case responsePayload.message === "User created":
          setState({
            registeredEmail: email,
            registering: false,
          });
          break;

        default:
          setState({
            registeredEmail: undefined,
            registerError: "An unknown error occurred",
            registering: false,
          });
      }
    } catch (error) {
      captureSentryException(error, "Failed to register");
    }
  };
}

function resetErrors(): Action<LoginStoreState> {
  return ({ setState }) => {
    setState({ loginError: "", registerError: "" });
  };
}

// -----------------------------------------------------------------------------

export interface LoginStoreState {
  // subpage toggles
  checkEmail: boolean;
  hasSSORedirect: boolean;
  registeredEmail?: string;
  registerError?: string;
  // interim state
  submitting: boolean;
  registering: boolean;
  loginError: string;
}
